import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, createVNode as _createVNode, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "book-food-page" }
const _hoisted_2 = { class: "page-header" }
const _hoisted_3 = { class: "flex flex-wrap gap-2" }
const _hoisted_4 = { class: "result-caption" }
const _hoisted_5 = { class: "stats-grid" }
const _hoisted_6 = { class: "summary-note" }

import { computed, onMounted, onBeforeUnmount, reactive, ref, watch } from 'vue';
  import { useRouter } from 'vue-router';
  import { showToast } from 'vant';
  import type { TableColumnsType } from 'ant-design-vue';
  import zhCN from 'ant-design-vue/es/locale/zh_CN';
  import dayjs, { Dayjs } from 'dayjs';
  import * as XLSX from 'xlsx';
  import { restoreSelectedShop, cacheSelectedShop } from '@/utils/selectedShop';
  import { fetchShopList } from '@/service/base';
  import { getBookOrderFood, getBookOrderFoodSum } from '@/service/bookOrderFood';
  import { getDeliveryOrderFood, getDeliveryOrderFoodSum } from '@/service/deliveryOrderFood';
  import {
    BookFoodShopModel,
    BookOrderFoodFilterModel,
    BookOrderFoodModel,
    BookOrderFoodSumModel,
  } from '@/model/bookOrderFood';
  import { aggregateBookOrderFood } from '@/utils/bookOrderFood';

  
export default /*@__PURE__*/_defineComponent({
  __name: 'KitchenDispatchView',
  props: {
    reportType: { default: 'booking' }
  },
  setup(__props: any) {

  const props = __props;
  const router = useRouter();
  const isDelivery = computed(() => props.reportType === 'delivery');
  const pageTitle = computed(() => (isDelivery.value ? '外卖菜品' : '预定菜品'));
  const dateLabel = computed(() => (isDelivery.value ? '外卖日期' : '预定日期'));
  const shops = ref<BookFoodShopModel[]>([]);
  const dateRange = ref<[Dayjs, Dayjs]>([
    dayjs().startOf('day'),
    dayjs().add(2, 'day').endOf('day'),
  ]);
  const filters = reactive<Omit<BookOrderFoodFilterModel, 'selfPickDateStart' | 'selfPickDateEnd'>>(
    {},
  );
  const rows = ref<BookOrderFoodModel[]>([]);
  const reportSummary = ref<BookOrderFoodSumModel | null>(null);
  const loading = ref(false);
  const exportLoading = ref(false);
  const error = ref('');
  const loadedCount = ref(0);
  const totalCount = ref(0);
  const activeTab = ref('summary');
  const resultCaption = ref('');
  const resultRange = ref('');
  let requestId = 0;
  let disposed = false;
  const orderStatusOptions = [
    { label: '已点餐', value: 1 },
    { label: '待出餐', value: 2 },
    { label: '已出餐', value: 8 },
    { label: '已完成', value: 10 },
    { label: '已取消', value: 999 },
  ];
  const paymentStatusOptions = [
    { label: '待支付', value: 0 },
    { label: '部分支付', value: 5 },
    { label: '已支付', value: 10 },
  ];
  const summaryPagination = reactive({
    current: 1,
    pageSize: 20,
    showSizeChanger: true,
    onChange: (page: number, size: number) => {
      summaryPagination.current = page;
      summaryPagination.pageSize = size;
    },
  });
  const detailPagination = reactive({
    current: 1,
    pageSize: 20,
    showSizeChanger: true,
    onChange: (page: number, size: number) => {
      detailPagination.current = page;
      detailPagination.pageSize = size;
    },
  });
  const summaryRows = computed(() => aggregateBookOrderFood(rows.value));
  const detailRows = computed(() => rows.value.map((row, index) => ({ ...row, key: index })));
  const formatNumber = (value: number): string =>
    value.toLocaleString('zh-CN', { maximumFractionDigits: 6 });
  const stats = computed(() => {
    const summary = reportSummary.value;
    return [
      {
        label: isDelivery.value ? '外卖天数' : '预定天数',
        value: new Set(summaryRows.value.map((row) => row.date)).size,
      },
      {
        label: '有效订单',
        value: summary ? summary.totalOrderCount - summary.cancelOrderCount : 0,
      },
      {
        label: '有效菜品数量',
        value: formatNumber(summary?.totalFoodQuantity ?? 0),
      },
      {
        label: '取消数量',
        value: formatNumber(summary?.cancelFoodQuantity ?? 0),
      },
      {
        label: '有效金额',
        value: `¥${(summary?.totalAmount ?? 0).toFixed(2)}`,
      },
    ];
  });
  const summaryFields = computed(() => [
    [dateLabel.value, 'date'],
    ['餐厅', 'shopName'],
    ['菜品分类', 'categoryName'],
    ['菜品名称', 'shopFoodName'],
    ['有效订单数', 'orderCount'],
    ['有效数量', 'quantity'],
    ['取消数量', 'cancelledQuantity'],
    ['有效金额', 'amount'],
  ]);
  const detailFields = computed(() => [
    [dateLabel.value, 'selfPickDate'],
    [isDelivery.value ? '外卖时间' : '预定时间', 'selfPickTime'],
    ...(isDelivery.value ? [['创建时间', 'createTime']] : []),
    ['订单编号', 'orderNo'],
    ['餐厅', 'shopName'],
    ['桌台', 'tableName'],
    ['菜品分类', 'categoryName'],
    ['菜品名称', 'shopFoodName'],
    ['规格', 'foodSpecName'],
    ['数量', 'quantity'],
    ['单价', 'price'],
    ['金额', 'amount'],
    ['订单状态', 'orderStatusName'],
    ['菜品状态', 'foodStatusName'],
    ['支付状态', 'paymentStatusName'],
    ['支付方式', 'paymentMethodName'],
    ...(!isDelivery.value ? [['创建时间', 'createTime']] : []),
  ]);
  const makeColumns = (fields: string[][]): TableColumnsType =>
    fields.map(([title, dataIndex]) => ({
      title,
      dataIndex,
      key: dataIndex,
      width: dataIndex === 'orderNo' || dataIndex === 'createTime' ? 180 : 125,
      customRender: ({ text }: { text: unknown }) => {
        if (['price', 'amount'].includes(dataIndex)) return `¥${Number(text || 0).toFixed(2)}`;
        if (dataIndex === 'createTime')
          return text ? dayjs(String(text)).format('YYYY-MM-DD HH:mm:ss') : '—';
        return text === undefined || text === null || text === '' ? '—' : String(text);
      },
    }));
  const summaryColumns = computed(() => makeColumns(summaryFields.value));
  const detailColumns = computed(() => makeColumns(detailFields.value));
  const setRange = (days: number): void => {
    dateRange.value = [
      dayjs().startOf('day'),
      dayjs()
        .add(days - 1, 'day')
        .endOf('day'),
    ];
  };
  const handleSearch = async (): Promise<void> => {
    if (loading.value) return;
    if (!filters.shopId) {
      rows.value = [];
      resultCaption.value = '';
      showToast('请选择餐厅');
      return;
    }
    if (
      !dateRange.value?.[0]?.isValid() ||
      !dateRange.value?.[1]?.isValid() ||
      dateRange.value[0].isAfter(dateRange.value[1])
    ) {
      showToast(`请选择有效的${dateLabel.value}范围`);
      return;
    }
    const id = ++requestId;
    const delivery = isDelivery.value;
    const data: BookOrderFoodFilterModel = {
      ...filters,
      orderNo: filters.orderNo?.trim() || undefined,
      shopFoodName: filters.shopFoodName?.trim() || undefined,
      categoryName: filters.categoryName?.trim() || undefined,
      orderStatuss: filters.orderStatuss?.length ? [...filters.orderStatuss] : undefined,
      paymentStatuss: filters.paymentStatuss?.length ? [...filters.paymentStatuss] : undefined,
      selfPickDateStart: dateRange.value[0].format('YYYY-MM-DD'),
      selfPickDateEnd: dateRange.value[1].format('YYYY-MM-DD'),
    };
    loading.value = true;
    error.value = '';
    rows.value = [];
    reportSummary.value = null;
    resultCaption.value = '';
    loadedCount.value = 0;
    totalCount.value = 0;
    try {
      const allRows: BookOrderFoodModel[] = [];
      let pageNum = 1;
      let expectedTotal: number | undefined;
      do {
        const response = delivery
          ? await getDeliveryOrderFood({ pageNum, pageSize: 500, data })
          : await getBookOrderFood({ pageNum, pageSize: 500, data });
        if (id !== requestId) return;
        if (response.code !== 200) throw new Error(response.msg || '查询失败');
        const result = response.result;
        if (!result || !Array.isArray(result.list) || !Number.isFinite(Number(result.total)))
          throw new Error('接口返回的数据格式异常');
        const total = Number(result.total);
        if (expectedTotal !== undefined && expectedTotal !== total)
          throw new Error('查询期间数据发生变化，请重新查询');
        expectedTotal = total;
        if (!result.list.length && allRows.length < total)
          throw new Error('明细未加载完整，请重新查询');
        allRows.push(...result.list);
        totalCount.value = total;
        loadedCount.value = allRows.length;
        pageNum += 1;
      } while (allRows.length < expectedTotal);
      const summaryResponse = delivery
        ? await getDeliveryOrderFoodSum({ pageNum: 1, pageSize: 500, data })
        : await getBookOrderFoodSum({ pageNum: 1, pageSize: 500, data });
      if (id !== requestId) return;
      if (summaryResponse.code !== 200 || !summaryResponse.result) {
        throw new Error(summaryResponse.msg || '查询汇总数据失败');
      }
      reportSummary.value = summaryResponse.result;
      rows.value = allRows;
      summaryPagination.current = 1;
      detailPagination.current = 1;
      resultRange.value = `${data.selfPickDateStart}_${data.selfPickDateEnd}`;
      const shopName =
        shops.value.find((shop) => shop.shopId === data.shopId)?.shopName || '未知餐厅';
      resultCaption.value = `${shopName} · ${dateLabel.value} ${data.selfPickDateStart} 至 ${data.selfPickDateEnd} · 共 ${allRows.length} 条明细`;
      cacheSelectedShop(data.shopId);
    } catch (cause) {
      if (id !== requestId) return;
      console.error(`查询${pageTitle.value}失败:`, cause);
      error.value =
        cause instanceof Error ? cause.message : `查询${pageTitle.value}失败，请稍后重试`;
    } finally {
      if (id === requestId) loading.value = false;
    }
  };
  const handleReset = (): void => {
    Object.assign(filters, {
      orderNo: undefined,
      shopFoodName: undefined,
      categoryName: undefined,
      orderStatuss: undefined,
      paymentStatuss: undefined,
    });
    setRange(3);
    void handleSearch();
  };
  const handleExport = (): void => {
    if (loading.value || !rows.value.length) return;
    exportLoading.value = true;
    try {
      const workbook = XLSX.utils.book_new();
      const appendSheet = (name: string, fields: string[][], data: object[]): void => {
        const sheet = XLSX.utils.aoa_to_sheet([
          fields.map(([title]) => title),
          ...data.map((row) => {
            const values = new Map(Object.entries(row));
            return fields.map(([, key]) => values.get(key) ?? '');
          }),
        ]);
        sheet['!cols'] = fields.map(() => ({ wch: 22 }));
        XLSX.utils.book_append_sheet(workbook, sheet, name);
      };
      appendSheet('每日菜品汇总', summaryFields.value, summaryRows.value);
      appendSheet('订单明细', detailFields.value, rows.value);
      const notes = XLSX.utils.aoa_to_sheet([
        ['查询范围', resultCaption.value],
        [
          '汇总口径',
          `按${dateLabel.value}、餐厅、分类、菜品汇总；有效数量、订单数及金额不含已取消订单和菜品。`,
        ],
        ['说明', '导出内容为最近一次成功查询的全部结果，明细包含取消项。'],
      ]);
      XLSX.utils.book_append_sheet(workbook, notes, '说明');
      XLSX.writeFile(workbook, `${pageTitle.value}_${resultRange.value}.xlsx`);
      showToast('导出成功');
    } catch (cause) {
      console.error(`导出${pageTitle.value}失败:`, cause);
      showToast('导出失败，请重试');
    } finally {
      exportLoading.value = false;
    }
  };
  watch(
    () => props.reportType,
    () => {
      requestId += 1;
      rows.value = [];
      reportSummary.value = null;
      error.value = '';
      resultCaption.value = '';
      resultRange.value = '';
      loadedCount.value = 0;
      totalCount.value = 0;
      loading.value = false;
      activeTab.value = 'summary';
      summaryPagination.current = 1;
      detailPagination.current = 1;
      Object.assign(filters, {
        orderNo: undefined,
        shopFoodName: undefined,
        categoryName: undefined,
        orderStatuss: undefined,
        paymentStatuss: undefined,
      });
      setRange(3);
      if (filters.shopId) void handleSearch();
    },
  );
  onMounted(async () => {
    try {
      const response = await fetchShopList({ pageNum: 1, pageSize: 999, data: { shopStatus: 0 } });
      if (disposed) return;
      if (response.code !== 200) throw new Error(response.msg || '加载餐厅失败');
      shops.value = response.result?.list || [];
      filters.shopId = restoreSelectedShop(shops.value);
    } catch (cause) {
      if (disposed) return;
      console.error('加载餐厅失败:', cause);
      showToast('加载餐厅失败，请刷新重试');
    }
    if (!disposed && filters.shopId) void handleSearch();
  });
  onBeforeUnmount(() => {
    disposed = true;
    requestId += 1;
  });

return (_ctx: any,_cache: any) => {
  const _component_a_select_option = _resolveComponent("a-select-option")!
  const _component_a_select = _resolveComponent("a-select")!
  const _component_a_form_item = _resolveComponent("a-form-item")!
  const _component_a_range_picker = _resolveComponent("a-range-picker")!
  const _component_a_input = _resolveComponent("a-input")!
  const _component_van_button = _resolveComponent("van-button")!
  const _component_a_form = _resolveComponent("a-form")!
  const _component_a_alert = _resolveComponent("a-alert")!
  const _component_a_table = _resolveComponent("a-table")!
  const _component_a_tab_pane = _resolveComponent("a-tab-pane")!
  const _component_a_tabs = _resolveComponent("a-tabs")!
  const _component_a_spin = _resolveComponent("a-spin")!
  const _component_a_card = _resolveComponent("a-card")!
  const _component_a_config_provider = _resolveComponent("a-config-provider")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_a_config_provider, { locale: _unref(zhCN) }, {
      default: _withCtx(() => [
        _createElementVNode("header", _hoisted_2, [
          _createElementVNode("button", {
            class: "back-button",
            "aria-label": "返回",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(router).back()))
          }, _cache[9] || (_cache[9] = [
            _createElementVNode("i", { class: "fas fa-arrow-left" }, null, -1)
          ])),
          _createElementVNode("h1", null, _toDisplayString(pageTitle.value), 1)
        ]),
        _createVNode(_component_a_card, { bordered: false }, {
          default: _withCtx(() => [
            _createVNode(_component_a_form, {
              model: filters,
              layout: "inline",
              class: "search-form flex flex-wrap",
              onFinish: handleSearch
            }, {
              default: _withCtx(() => [
                _createVNode(_component_a_form_item, {
                  label: "餐厅",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_select, {
                      value: filters.shopId,
                      "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((filters.shopId) = $event)),
                      placeholder: "请选择餐厅",
                      disabled: "",
                      class: "filter-input",
                      onChange: _unref(cacheSelectedShop)
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(shops.value, (shop) => {
                          return (_openBlock(), _createBlock(_component_a_select_option, {
                            key: shop.shopId,
                            value: shop.shopId
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(shop.shopName), 1)
                            ]),
                            _: 2
                          }, 1032, ["value"]))
                        }), 128))
                      ]),
                      _: 1
                    }, 8, ["value", "onChange"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, {
                  label: dateLabel.value,
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_range_picker, {
                      value: dateRange.value,
                      "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((dateRange).value = $event)),
                      "allow-clear": false,
                      class: "date-input"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }, 8, ["label"]),
                _createVNode(_component_a_form_item, { label: "菜品" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: filters.shopFoodName,
                      "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((filters.shopFoodName) = $event)),
                      placeholder: "输入菜品名称",
                      "allow-clear": "",
                      class: "filter-input"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, { label: "分类" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: filters.categoryName,
                      "onUpdate:value": _cache[4] || (_cache[4] = ($event: any) => ((filters.categoryName) = $event)),
                      placeholder: "输入分类名称",
                      "allow-clear": "",
                      class: "filter-input"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, { label: "订单编号" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: filters.orderNo,
                      "onUpdate:value": _cache[5] || (_cache[5] = ($event: any) => ((filters.orderNo) = $event)),
                      placeholder: "输入订单编号",
                      "allow-clear": "",
                      class: "filter-input"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, { label: "订单状态" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_select, {
                      value: filters.orderStatuss,
                      "onUpdate:value": _cache[6] || (_cache[6] = ($event: any) => ((filters.orderStatuss) = $event)),
                      mode: "multiple",
                      "max-tag-count": 1,
                      options: orderStatusOptions,
                      placeholder: "全部状态",
                      "allow-clear": "",
                      class: "filter-input"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, { label: "支付状态" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_select, {
                      value: filters.paymentStatuss,
                      "onUpdate:value": _cache[7] || (_cache[7] = ($event: any) => ((filters.paymentStatuss) = $event)),
                      mode: "multiple",
                      "max-tag-count": 1,
                      options: paymentStatusOptions,
                      placeholder: "全部状态",
                      "allow-clear": "",
                      class: "filter-input"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, null, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_3, [
                      _createVNode(_component_van_button, {
                        type: "primary",
                        size: "small",
                        "native-type": "submit",
                        loading: loading.value
                      }, {
                        default: _withCtx(() => _cache[10] || (_cache[10] = [
                          _createTextVNode("查询", -1)
                        ])),
                        _: 1,
                        __: [10]
                      }, 8, ["loading"]),
                      _createVNode(_component_van_button, {
                        size: "small",
                        disabled: loading.value,
                        onClick: handleReset
                      }, {
                        default: _withCtx(() => _cache[11] || (_cache[11] = [
                          _createTextVNode("重置", -1)
                        ])),
                        _: 1,
                        __: [11]
                      }, 8, ["disabled"]),
                      _createVNode(_component_van_button, {
                        type: "success",
                        size: "small",
                        disabled: loading.value || !rows.value.length,
                        loading: exportLoading.value,
                        onClick: handleExport
                      }, {
                        default: _withCtx(() => _cache[12] || (_cache[12] = [
                          _createTextVNode("导出 Excel", -1)
                        ])),
                        _: 1,
                        __: [12]
                      }, 8, ["disabled", "loading"])
                    ])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["model"]),
            (error.value)
              ? (_openBlock(), _createBlock(_component_a_alert, {
                  key: 0,
                  message: error.value,
                  type: "error",
                  "show-icon": "",
                  class: "mb-4"
                }, null, 8, ["message"]))
              : _createCommentVNode("", true),
            _createVNode(_component_a_spin, {
              spinning: loading.value,
              tip: `正在加载明细 ${loadedCount.value} / ${totalCount.value} 条`
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_4, _toDisplayString(resultCaption.value), 1),
                _createElementVNode("div", _hoisted_5, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(stats.value, (stat) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: stat.label,
                      class: "stat-card"
                    }, [
                      _createElementVNode("span", null, _toDisplayString(stat.label), 1),
                      _createElementVNode("strong", null, _toDisplayString(stat.value), 1)
                    ]))
                  }), 128))
                ]),
                _createVNode(_component_a_tabs, {
                  "active-key": activeTab.value,
                  "onUpdate:activeKey": _cache[8] || (_cache[8] = ($event: any) => ((activeTab).value = $event))
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_tab_pane, {
                      key: "summary",
                      tab: "每日菜品汇总"
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_6, "按" + _toDisplayString(dateLabel.value) + "、餐厅、分类、菜品汇总；有效数量和金额不含已取消订单及菜品。", 1),
                        _createVNode(_component_a_table, {
                          columns: summaryColumns.value,
                          "data-source": summaryRows.value,
                          "row-key": "key",
                          pagination: summaryPagination,
                          scroll: { x: 1000 },
                          size: "middle"
                        }, null, 8, ["columns", "data-source", "pagination"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_a_tab_pane, {
                      key: "detail",
                      tab: `订单明细（${rows.value.length}）`
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_a_table, {
                          columns: detailColumns.value,
                          "data-source": detailRows.value,
                          "row-key": "key",
                          pagination: detailPagination,
                          scroll: { x: 2000 },
                          size: "middle"
                        }, null, 8, ["columns", "data-source", "pagination"])
                      ]),
                      _: 1
                    }, 8, ["tab"])
                  ]),
                  _: 1
                }, 8, ["active-key"])
              ]),
              _: 1
            }, 8, ["spinning", "tip"])
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["locale"])
  ]))
}
}

})